import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Clock, Check, Beef, Croissant, ShoppingCart, Pill, Apple, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { cn } from '@/utils/cn';
import { PlaylistPreviewModal } from '@/components/preview/PlaylistPreviewModal';

const SEGMENTS = [
  { value: 'all',      label: 'Todos',        icon: Sparkles },
  { value: 'acougue',  label: 'Açougue',      icon: Beef },
  { value: 'padaria',  label: 'Padaria',      icon: Croissant },
  { value: 'mercado',  label: 'Mercado',      icon: ShoppingCart },
  { value: 'farmacia', label: 'Farmácia',     icon: Pill },
];

// Temas visuais hardcoded por código de template (visual da prévia)
const TEMPLATE_PREVIEWS: Record<string, { bg: string; accent: string; icon: any; emoji: string }> = {
  'acougue-premium':   { bg: 'from-red-900 to-red-700',     accent: '#FFD700', icon: Beef,        emoji: '🥩' },
  'acougue-express':   { bg: 'from-red-700 to-red-500',     accent: '#FFFFFF', icon: Beef,        emoji: '🥩' },
  'padaria-aconchego': { bg: 'from-amber-700 to-orange-600',accent: '#FFFBEB', icon: Croissant,   emoji: '🥖' },
  'padaria-manha':     { bg: 'from-amber-500 to-yellow-400',accent: '#7C2D12', icon: Croissant,   emoji: '☕' },
  'mercado-classico':  { bg: 'from-red-600 to-red-800',     accent: '#FFD600', icon: ShoppingCart,emoji: '🛒' },
  'farmacia-saude':    { bg: 'from-green-700 to-emerald-600',accent: '#A7F3D0', icon: Pill,       emoji: '💊' },
  'hortifruti-fresco': { bg: 'from-green-600 to-lime-500',  accent: '#FFFFFF', icon: Apple,       emoji: '🥬' },
};

export function TemplatesGalleryPage() {
  const navigate = useNavigate();
  const [segment, setSegment] = useState<string>('all');
  const [previewCode, setPreviewCode] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['playlist-templates', segment],
    queryFn: () => apiClient.getPlaylistTemplates(segment === 'all' ? undefined : segment).then(r => r.data),
  });

  const { data: previewData } = useQuery({
    queryKey: ['playlist-template', previewCode],
    queryFn: () => apiClient.getPlaylistTemplate(previewCode!).then(r => r.data),
    enabled: !!previewCode,
  });

  const useMut = useMutation({
    mutationFn: (code: string) => apiClient.usePlaylistTemplate(code),
    onSuccess: (r) => {
      toast.success('Playlist criada! Personalize do seu jeito 🎉');
      navigate(`/playlists/${r.data.playlist_id}`);
    },
    onError: () => toast.error('Erro ao criar playlist a partir do modelo'),
  });

  return (
    <div className="container-app py-8">
      <Link to="/playlists" className="inline-flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <div className="text-center max-w-2xl mx-auto mb-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-brand-100 text-brand-800 px-4 py-1.5 text-sm font-medium mb-4">
          <Sparkles className="h-3.5 w-3.5" /> Modelos prontos
        </div>
        <h1 className="text-4xl font-extrabold tracking-tight">
          Comece com 1 click
        </h1>
        <p className="mt-3 text-muted-foreground">
          Escolha um modelo do seu segmento. Já vem com slides, preços e promoções estruturados.
          Você só edita o que quiser. Vamos lá?
        </p>
      </div>

      {/* Filtro segmentos */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {SEGMENTS.map((s) => (
          <button
            key={s.value}
            onClick={() => setSegment(s.value)}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all',
              segment === s.value
                ? 'bg-brand-600 text-white shadow-md scale-105'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            )}
          >
            <s.icon className="h-4 w-4" />
            {s.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Carregando modelos…</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">Nenhum modelo encontrado pra esse segmento</div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((t: any) => (
            <TemplateCard
              key={t.id}
              template={t}
              onUse={() => useMut.mutate(t.code)}
              onPreview={() => setPreviewCode(t.code)}
              loading={useMut.isPending && useMut.variables === t.code}
            />
          ))}
        </div>
      )}

      {/* Modal de preview */}
      {previewCode && previewData && (
        <PlaylistPreviewModal
          slides={typeof previewData.slides === 'string' ? JSON.parse(previewData.slides) : previewData.slides}
          title={previewData.name}
          onClose={() => setPreviewCode(null)}
          onUseTemplate={() => {
            const code = previewCode;
            setPreviewCode(null);
            useMut.mutate(code);
          }}
          useButtonLabel="Usar este modelo"
        />
      )}
    </div>
  );
}

function TemplateCard({ template, onUse, onPreview, loading }: any) {
  const preview = TEMPLATE_PREVIEWS[template.code] ?? {
    bg: 'from-gray-700 to-gray-900', accent: '#FFFFFF', icon: Sparkles, emoji: '✨',
  };
  const Icon = preview.icon;

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all group">
      {/* Preview visual estilizado */}
      <div className={cn('relative h-48 bg-gradient-to-br p-6 flex flex-col justify-between', preview.bg)}>
        {template.badge && (
          <div className="absolute top-3 right-3">
            <Badge variant="default" className="bg-white/20 text-white backdrop-blur border-0 shadow-lg">
              {template.badge}
            </Badge>
          </div>
        )}

        <div className="flex items-start gap-3">
          <div className="grid place-items-center h-12 w-12 rounded-xl bg-white/15 backdrop-blur">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="text-5xl">{preview.emoji}</div>
        </div>

        <div>
          <h3 className="text-2xl font-extrabold text-white tracking-tight">
            {template.name}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-white/80 text-xs">
            <span className="inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> {template.slide_count} slides
            </span>
            <span className="capitalize">• {template.segment}</span>
          </div>
        </div>
      </div>

      {/* Info + CTA */}
      <div className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
          {template.description}
        </p>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          Pronto em segundos
          <Check className="h-3 w-3 text-emerald-500 ml-2" />
          Editável depois
        </div>

        <div className="flex gap-2">
          <Button
            onClick={onPreview}
            variant="outline"
            className="flex-1"
          >
            <Eye className="h-4 w-4" />
            Pré-visualizar
          </Button>
          <Button
            onClick={onUse}
            loading={loading}
            variant="gradient"
            className="flex-1"
          >
            Usar
          </Button>
        </div>
      </div>
    </Card>
  );
}
